/**
 * Utility functions for handling YouTube URLs and embeds
 */

/**
 * Process a YouTube URL or ID and extract relevant information
 * @param {string} urlOrId - YouTube URL or video ID
 * @returns {Object} Object containing videoId, isShort flag, and optional error
 */
export function processYouTubeUrl(urlOrId) {
  if (!urlOrId) {
    return {
      videoId: null,
      isShort: false,
      error: 'No URL or ID provided'
    };
  }

  // If it's already a valid video ID (11 characters), return it
  if (/^[a-zA-Z0-9_-]{11}$/.test(urlOrId)) {
    return {
      videoId: urlOrId,
      isShort: false // Cannot determine from ID alone
    };
  }
  
  // Extract from various YouTube URL formats
  
  // Shorts URL format
  const shortsRegex = /(?:youtube\.com\/shorts\/|youtu\.be\/shorts\/)([a-zA-Z0-9_-]{11})/i;
  const shortsMatch = urlOrId.match(shortsRegex);
  if (shortsMatch) {
    return {
      videoId: shortsMatch[1],
      isShort: true
    };
  }
  
  // Live URL format (e.g., https://www.youtube.com/live/VIDEOID)
  const liveRegex = /(?:youtube\.com\/live\/)([a-zA-Z0-9_-]{11})/i;
  const liveMatch = urlOrId.match(liveRegex);
  if (liveMatch) {
    return {
      videoId: liveMatch[1],
      isShort: false
    };
  }
  
  // Standard YouTube URL formats
  const standardRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/i;
  const standardMatch = urlOrId.match(standardRegex);
  if (standardMatch) {
    return {
      videoId: standardMatch[1],
      isShort: urlOrId.toLowerCase().includes('/shorts/')
    };
  }
  
  // Invalid URL or ID
  return {
    videoId: null,
    isShort: false,
    error: 'Invalid YouTube URL or ID'
  };
}

/**
 * Extract YouTube video ID from URL
 * @param {string} url - YouTube URL
 * @returns {string|null} Video ID or null if not valid
 */
export function extractVideoId(url) {
  if (!url) return null;
  
  const result = processYouTubeUrl(url);
  return result.videoId;
}

/**
 * Check if a YouTube URL is a Shorts video
 * @param {string} url - YouTube URL
 * @returns {boolean} True if URL is for a YouTube Short
 */
export function isYouTubeShort(url) {
  if (!url) return false;
  
  // Check if URL contains shorts patterns
  if (url.toLowerCase().includes('/shorts/')) {
    return true;
  }
  
  // Check if URL contains vertical/portrait indicators
  if (url.toLowerCase().includes('vertical') || 
      url.toLowerCase().includes('portrait')) {
    return true;
  }
  
  // Use the processor to check if it's a shorts URL
  const result = processYouTubeUrl(url);
  return result.isShort;
}

/**
 * Generate YouTube embed URL with options
 * @param {string} videoId - YouTube video ID
 * @param {Object} options - Embed options
 * @returns {string} Embed URL
 */
export function generateEmbedUrl(videoId, options = {}) {
  if (!videoId) return '';
  
  const baseUrl = `https://www.youtube.com/embed/${videoId}`;
  
  // Build query parameters
  const params = [];
  
  if (options.autoplay) params.push('autoplay=1');
  if (options.mute) params.push('mute=1');
  if (options.loop) params.push('loop=1');
  if (options.playlistId) params.push(`playlist=${options.playlistId}`);
  if (options.startTime) params.push(`start=${options.startTime}`);
  if (options.endTime) params.push(`end=${options.endTime}`);
  
  // Add UI options
  if (options.controls === false) params.push('controls=0');
  if (options.showInfo === false) params.push('showinfo=0');
  if (options.modestBranding !== false) params.push('modestbranding=1');
  if (options.rel === false) params.push('rel=0');
  
  // Build the final URL
  return params.length > 0 ? `${baseUrl}?${params.join('&')}` : baseUrl;
}
